import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'libs.nucleus';
import { normalizeCubeFilterParam } from '../../../dashboards/utils/dashboard_filters.utils';
import { ReportAnalyticsEvents } from '../../constants/report_analytics_events.constants';
import { ViewFilter } from '../report_chart/report_chart_view_filter.component';
export const DataReportGlobalFilters = ({ isLoading, dashboardData, setFilterParams, logReportEvent, disabled = false, }) => {
    const [selectedFilters, setSelectedFilters] = useState({});
    const [selectedOperators, setSelectedOperators] = useState({});
    const hasAppliedDefaults = useRef(false);
    const filters = dashboardData?.filters ?? [];
    const operatorByKey = useMemo(() => {
        const map = {};
        for (const f of filters) {
            map[f.key] = f.defaultOperator || f.operator || 'equals';
        }
        return map;
    }, [filters]);
    // Pre-apply default filter values on first render
    useEffect(() => {
        if (hasAppliedDefaults.current || !filters.length) {
            return;
        }
        hasAppliedDefaults.current = true;
        const defaults = {};
        const defaultParams = [];
        for (const f of filters) {
            if (f.defaultValues?.length) {
                defaults[f.key] = f.defaultValues.map((v) => ({ label: v, value: v }));
                // Skip legacy persisted defaults that don't provide every required slot
                // (e.g. `inDateRange` requires both a from and a to value) so Cube.js
                // doesn't reject the query.
                if (f.defaultValues.every((v) => v)) {
                    defaultParams.push(normalizeCubeFilterParam({
                        member: f.key,
                        operator: f.defaultOperator || 'equals',
                        values: f.defaultValues,
                    }));
                }
            }
        }
        setSelectedOperators({ ...operatorByKey });
        if (Object.keys(defaults).length > 0) {
            setSelectedFilters(defaults);
            setFilterParams(defaultParams);
        }
    }, [filters, setFilterParams, operatorByKey]);
    const applyGlobalFilters = () => {
        const filterParams = Object.entries(selectedFilters)
            // Range operators keep two slots (from / to); drop a filter when the user
            // has only supplied one of them so we never send `[value, '']` to Cube.js.
            .filter(([, value]) => Array.isArray(value) && value.length > 0 && value.every((v) => v.value))
            .map(([key, value]) => normalizeCubeFilterParam({
            member: key,
            operator: selectedOperators[key] || operatorByKey[key] || 'equals',
            values: value.map((v) => v.value),
        }));
        setFilterParams(filterParams);
        if (filterParams.length > 0) {
            logReportEvent?.(ReportAnalyticsEvents.FILTER_APPLIED, {
                filter_type: 'dashboard_global',
                filters_applied: filterParams.map((f) => f.member).join(','),
                filter_count: filterParams.length,
            });
        }
    };
    const clearGlobalFilters = () => {
        setSelectedFilters({});
        setSelectedOperators({ ...operatorByKey });
        setFilterParams([]);
    };
    const hasFilters = !isLoading &&
        dashboardData &&
        dashboardData.filters &&
        Array.isArray(dashboardData.filters) &&
        dashboardData.filters.length > 0;
    if (!hasFilters) {
        return null;
    }
    return (_jsxs("div", { className: 'flex md:items-end justify-between mb-8 gap-8 flex-col md:flex-row flex-wrap', "data-testid": 'global-filters', "data-disabled": String(disabled), children: [_jsx("fieldset", { disabled: disabled, className: 'flex-1 flex gap-4 flex-col md:flex-row flex-wrap', children: filters.map((filter) => (_jsx(ViewFilter, { filterData: filter, selectedOptions: selectedFilters[filter.key] || [], setSelectedOptions: (options) => setSelectedFilters((prev) => ({ ...prev, [filter.key]: options })), selectedOperator: selectedOperators[filter.key] || operatorByKey[filter.key] || 'equals', onOperatorChange: (operator) => setSelectedOperators((prev) => ({ ...prev, [filter.key]: operator })), logReportEvent: logReportEvent }, filter.key))) }), _jsxs("div", { className: 'flex items-center gap-2', children: [_jsx(Button, { label: 'Clear filters', variant: 'transparent', onClick: clearGlobalFilters, disabled: disabled }), _jsx(Button, { label: 'Apply filters', variant: 'secondary', onClick: applyGlobalFilters, disabled: disabled })] })] }));
};
